import { usePrevious } from '@dnd-kit/utilities';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchIcon from '@mui/icons-material/Search';
import { Chip, CircularProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { GridColDef } from '@mui/x-data-grid-premium';
import currency from 'currency.js';
import get from 'lodash-es/get';
import React, { useEffect, useState } from 'react';
import { generatePath, NavLink } from 'react-router-dom';

import { EDIT_LINE_ITEM_URL } from '../../constants/routeUrls';
import useContractLineItemData from '../../hooks/useContractLineItemData';
import { useContractManagerContext } from '../../hooks/useContractManagerContext';
import useLineItems from '../../hooks/useLineItems';
import { addCommas, isBlank } from '../../util/helpers';
import { isAggregateCell } from '../../util/tableUtils';
import DataGrid from '../shared/DataGrid';
import StatusChip from '../shared/StatusChip';
import { formatDate, formatFlightDate, formatImpressionBudget } from './KPIHeader';

function LineItemEditLink({ params }: { params: any }) {
  const { contract } = useContractManagerContext();
  const { contractId } = contract;

  return (
    <NavLink to={generatePath(EDIT_LINE_ITEM_URL, { id: contractId.toString(), lineItemId: params.row.id.toString() })}>
      <Tooltip title="Edit Line Item" placement="top-end">
        <FontAwesomeIcon icon={faPenToSquare} color="rgba(0, 0, 0, 0.54)" />
      </Tooltip>
    </NavLink>
  );
}

interface ContractLineItemDataProps {
  setCount?: (count: number) => void;
}

function ContractLineItemData({ setCount }: ContractLineItemDataProps) {
  const {
    contract,
    selectedLineItemIds,
    setSelectedLineItemIds,
    setSelectedLineItems,
    selectedAdGroupIds,
    setSelectedAdGroupIds,
    isLoading: defaultIsLoading,
    setActiveTab,
    setTabIcons,
  } = useContractManagerContext();
  const { contractId, pricingRate: contractPricingRate, multiRate } = contract;

  const lineItemsQuery = useLineItems(contractId);
  const contractLineItemDataQuery = useContractLineItemData(contractId);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const { data: contractLineItemDataJson } = contractLineItemDataQuery;

  const { data: lineItemsJson } = lineItemsQuery;

  const isSuccess = contractLineItemDataQuery.isSuccess && lineItemsQuery.isSuccess;
  const isLoading = contractLineItemDataQuery.isLoading || lineItemsQuery.isLoading || defaultIsLoading;

  const lineItemsData = get(lineItemsJson, 'collection', []);
  const contractLineItemData = get(contractLineItemDataJson, 'data', []);

  const onClickName = (e, lineItemId: number) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedLineItemIds([lineItemId]);

    // Need to set timeout to allow the state to update before switching tabs
    setTimeout(() => {
      setActiveTab('Ad Groups');
    }, 1);
  };

  const formatName = (name, lineItemId) => (
    <Stack direction="row" onClick={(e) => onClickName(e, lineItemId)} sx={{ cursor: 'pointer' }}>
      {name}
    </Stack>
  );

  const round = (value) => (!isBlank(value) && addCommas(currency(value, { symbol: '', precision: 2 }).value)) || '--';

  const customFormatImpressionBudget = (advisoryBudget, bonus) => round(formatImpressionBudget(advisoryBudget, bonus));

  const formatPricingRate = (value) => {
    // contractPricingRate is already divided by 100
    let pricingRate = Number(contractPricingRate);
    if (multiRate) {
      // But pricing by line item is not divided by 100 yet
      pricingRate = value / 100;
    }

    return round(pricingRate);
  };

  const formatStatus = (uiState, deliveryStatus) => <StatusChip status={uiState} subStatus={deliveryStatus} />;

  const columnData: GridColDef[] = [
    {
      headerName: 'Line Item Name',
      field: 'name',
      renderCell: (params) => !isAggregateCell(params) && formatName(params.value, params.row.id),
      // First column cannot use flex or else when it is the active tab.
      // Must set a width for it to display properly again.
      width: 400,
    },
    {
      headerName: 'Action',
      field: 'actions',
      renderCell: (params) => !isAggregateCell(params) && <LineItemEditLink params={params} />,
      display: 'flex',
      flex: 1,
      type: 'actions',
    },
    {
      headerName: 'Flight Dates',
      field: 'start_date',
      renderCell: (params) => !isAggregateCell(params) && formatFlightDate(params.value, params.row.end_date),
      flex: 2,
    },
    {
      headerName: 'Rate',
      field: 'pricing_rate',
      renderCell: (params) => !isAggregateCell(params) && `$${formatPricingRate(params.value)}`,
      flex: 1,
    },
    {
      headerName: 'Impression Budget',
      field: 'advisory_budget',
      renderCell: (params) => !isAggregateCell(params) && customFormatImpressionBudget(params.value, params.row.bonus),
      flex: 1,
    },
    {
      headerName: 'Impressions',
      field: 'y1',
      renderCell: (params) => !isAggregateCell(params) && round(params.value),
      flex: 1,
    },
    {
      headerName: 'Pacing',
      field: 'y2',
      renderCell: (params) => !isAggregateCell(params) && round(params.value),
      flex: 1,
    },
    {
      headerName: 'CTR',
      field: 'y3',
      renderCell: (params) => !isAggregateCell(params) && `${round(params.value)}%`,
      flex: 1,
    },
    {
      headerName: 'Margin',
      field: 'y4',
      renderCell: (params) => !isAggregateCell(params) && round(params.value),
      flex: 1,
    },
    {
      headerName: '3P Discrepancy',
      field: 'y5',
      renderCell: (params) => !isAggregateCell(params) && `${round(params.value)}%`,
      flex: 1,
    },
    {
      headerName: 'Status',
      field: 'ui_state',
      renderCell: (params) => !isAggregateCell(params) && formatStatus(params.value, params.row.delivery_status),
      display: 'flex',
      flex: 2,
    },
  ];

  const rows = lineItemsData
    .map((lineItemDataItem) => ({
      ...lineItemDataItem,
      ...contractLineItemData.find(
        (contractLineItemDataItem) => contractLineItemDataItem.insertion_order_id === lineItemDataItem.id
      ),
      // Make it easier to search on the dates
      searchStartDate: formatDate(lineItemDataItem.start_date),
      searchEndDate: formatDate(lineItemDataItem.end_date),
    }))
    ?.map((dataItem) => ({
      ...dataItem,
      searchData: Object.values(dataItem).join(' '),
    }))
    .filter((item) => item.searchData.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0);

  const previousRowCount = usePrevious(rows?.length);

  useEffect(() => {
    if (rows?.length !== previousRowCount) {
      setCount(rows?.length);
    }
  }, [rows?.length, setCount, previousRowCount]);

  // Try storing the selected line item IDs sorted as a stringified array in local state to avoid re-renders
  const stringifiedSelectedLineItemIds = JSON.stringify(
    Array.from(selectedLineItemIds).sort((a, b) => Number(a) - Number(b))
  );
  const previousStringifiedSelectedLineItemIds = usePrevious(stringifiedSelectedLineItemIds);
  const isChangedSelectedLineItemIds = stringifiedSelectedLineItemIds !== previousStringifiedSelectedLineItemIds;

  useEffect(() => {
    if (!isChangedSelectedLineItemIds) {
      return;
    }
    // If the selectedLinesItemsIds have changed, then set the selectedLineItems
    const selectedLineItems = rows.filter((row) => selectedLineItemIds.includes(row.id));
    setSelectedLineItems(selectedLineItems);
  }, [isChangedSelectedLineItemIds, selectedLineItemIds, rows, setSelectedLineItems]);

  // If the selected line items have changed, then set the tab icons to have the names of the selected line items
  const firstSelectedLineItemId = selectedLineItemIds.length > 0 ? selectedLineItemIds[0] : null;
  const firstLineItem = rows.find((li) => li.id === firstSelectedLineItemId);
  const selectedLineItemIdsCount = selectedLineItemIds.length;

  useEffect(() => {
    if (!isChangedSelectedLineItemIds) {
      return;
    }

    const onDelete = () => {
      setSelectedLineItemIds([]);
      setActiveTab('Line Items');
    };

    if (selectedLineItemIdsCount > 1) {
      setTabIcons((prev) => ({
        ...prev,
        'Ad Groups': (
          <Chip
            label={`${selectedLineItemIdsCount} Line Items`}
            variant="outlined"
            color="primary"
            onDelete={onDelete}
          />
        ),
        // If there are not ad groups selected, then we are filtering by the selected line items
        Creatives:
          selectedAdGroupIds.length === 0 ? (
            <Chip
              label={`${selectedLineItemIdsCount} Line Items`}
              variant="outlined"
              color="primary"
              onDelete={onDelete}
            />
          ) : (
            prev.Creatives
          ),
      }));
    } else if (selectedLineItemIdsCount === 1) {
      setTabIcons((prev) => ({
        ...prev,
        'Ad Groups': (
          <Chip
            label={firstLineItem?.name}
            variant="outlined"
            color="primary"
            onDelete={onDelete}
            sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}
          />
        ),
        // If there are not ad groups selected, then we are filtering by the selected line items
        Creatives:
          selectedAdGroupIds.length === 0 ? (
            <Chip
              label={firstLineItem?.name}
              variant="outlined"
              color="primary"
              onDelete={onDelete}
              sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}
            />
          ) : (
            prev.Creatives
          ),
      }));
    } else {
      setTabIcons((prev) => ({
        ...prev,
        'Ad Groups': null,
      }));

      // Clear selected ad groups to force a refresh of the ad groups in case
      if (selectedAdGroupIds.length > 0) {
        setSelectedAdGroupIds([]);
        setTimeout(() => {
          setSelectedAdGroupIds(selectedAdGroupIds);
        }, 1);
      } else {
        // Also clear the Creatives tab icon if no ad groups are selected
        setTabIcons((prev) => ({
          ...prev,
          Creatives: null,
        }));
      }
    }
  }, [
    setActiveTab,
    isChangedSelectedLineItemIds,
    selectedLineItemIdsCount,
    firstLineItem,
    rows,
    setTabIcons,
    setSelectedLineItemIds,
    selectedAdGroupIds,
    setSelectedAdGroupIds,
  ]);

  return (
    <Stack spacing={2}>
      <TextField
        sx={{ backgroundColor: 'white', width: '100%' }}
        variant="outlined"
        placeholder="Search by Line Item Name, Flight Dates, Rate, Impression Budget, Impressions, Pacing, CTR, Margin, 3P Discrepency, or Status"
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        }}
        value={searchTerm}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1 }} />,
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? <CircularProgress color="inherit" size={24} /> : null}
            </InputAdornment>
          ),
        }}
      />
      <DataGrid
        loading={isLoading}
        rows={rows || []}
        columns={columnData}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectedLineItemIds}
        onRowSelectionModelChange={setSelectedLineItemIds}
        groupingColDef={{
          display: 'flex',
        }}
        isRowSelectable={(params) => !!params.row.id} // Only allow selection if the row has an id (not a group row)  
        disableAggregation
      />
      {isSuccess && lineItemsData?.length === 0 && <Typography>No data</Typography>}
    </Stack>
  );
}

export default ContractLineItemData;
